import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useReferral } from '../context/ReferralContext';
import { doc, setDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { checkUsernameExists } from '../services/userService';

// High-quality cricket images from Unsplash
const cricketStadiumImg = "https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1005&q=80";

// Generate referral code based on user ID (copied from ReferralContext.js)
const generateReferralCode = (uid) => {
  // Take first 6 characters of UID and add random 4 characters
  const prefix = uid.substring(0, 6);
  const randomChars = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `${prefix}${randomChars}`;
};

const Register = () => {
  const [username, setUsername] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [referralMessage, setReferralMessage] = useState('');
  const [isOver18, setIsOver18] = useState(false);
  const [step, setStep] = useState(1); // 1: choose provider, 2: enter username
  const [userCredential, setUserCredential] = useState(null);
  
  const { signInWithGoogle, signInWithApple } = useAuth();
  const { applyReferralCode } = useReferral();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Extract referral code from URL if present
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const refCode = params.get('ref');
    
    if (refCode) {
      setReferralCode(refCode);
      setReferralMessage('Referral code applied from link');
    }
  }, [location]);
  
  const handleGoogleSignUp = async () => {
    try {
      setLoading(true);
      setError('');
      console.log("Starting Google sign-up process");
      const result = await signInWithGoogle();
      console.log("Google auth successful, user:", result.user.uid);
      setUserCredential(result);
      setStep(2);
    } catch (error) {
      console.error("Google sign-up failed:", error);
      if (error.code === 'auth/popup-closed-by-user') {
        setError('Sign-up was cancelled. Please try again.');
      } else if (error.code === 'auth/popup-blocked') {
        setError('Popup was blocked by your browser. Please enable popups for this site and try again.');
      } else {
        setError(error.message || 'Failed to sign up with Google. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleAppleSignUp = async () => {
    try {
      setLoading(true);
      setError('');
      console.log("Starting Apple sign-up process");
      const result = await signInWithApple();
      console.log("Apple auth successful, user:", result.user.uid);
      setUserCredential(result);
      setStep(2);
    } catch (error) {
      console.error("Apple sign-up failed:", error);
      if (error.code === 'auth/popup-closed-by-user') {
        setError('Sign-up was cancelled. Please try again.');
      } else if (error.code === 'auth/popup-blocked') {
        setError('Popup was blocked by your browser. Please enable popups for this site and try again.');
      } else {
        setError(error.message || 'Failed to sign up with Apple. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmitUsername = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate input
    if (!username) {
      setError('Please enter a username');
      return;
    }
    
    if (username.length < 3) {
      setError('Username should be at least 3 characters');
      return;
    }
    
    if (!isOver18) {
      setError('You must confirm that you are at least 18 years old');
      return;
    }
    
    // Check if username already exists
    try {
      setLoading(true);
      const usernameExists = await checkUsernameExists(username);
      
      if (usernameExists) {
        setError('Username already exists. Please choose another one.');
        setLoading(false);
        return;
      }
      
      // Update user document with username and referral code
      const userRef = doc(db, 'users', userCredential.user.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        await setDoc(userRef, {
          username: username,
          displayName: username,
          referralCode: generateReferralCode(userCredential.user.uid),
          updatedAt: serverTimestamp()
        }, { merge: true });
      
        // Apply referral code if provided
        if (referralCode) {
      try {
            await applyReferralCode(referralCode);
          } catch (referralError) {
            console.error("Error applying referral code:", referralError);
          }
        }
        
        // Redirect to dashboard
        navigate('/user/home');
        } else {
        setError('User document not found. Please try again.');
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      setError(error.message || 'Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Step 1: Choose Sign-up Provider
  if (step === 1) {
  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-gray-50 dark:bg-gray-900">
      {/* Left Side - Image */}
      <div className="w-full md:w-1/2 bg-blue-600 flex items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-700 to-blue-900 opacity-90 z-0"></div>
        <div 
          className="absolute inset-0 z-0 bg-cover bg-center opacity-40" 
          style={{ backgroundImage: `url(${cricketStadiumImg})` }}
        ></div>
        
        <div className="relative z-10 max-w-lg px-6 py-12 text-center text-white">
          <h1 className="text-5xl font-bold mb-4">GamePreBattle</h1>
          <div className="w-16 h-1 bg-white mx-auto mb-8"></div>
          <p className="text-xl mb-10">Join the Cricket Prediction Community</p>
          <div className="flex flex-col space-y-4 mb-8">
            <div className="flex items-center bg-blue-700 bg-opacity-40 p-4 rounded-lg">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center mr-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="text-left">Make predictions for upcoming cricket matches</p>
            </div>
            <div className="flex items-center bg-blue-700 bg-opacity-40 p-4 rounded-lg">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center mr-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="text-left">Earn coins and rewards for accurate predictions</p>
            </div>
            <div className="flex items-center bg-blue-700 bg-opacity-40 p-4 rounded-lg">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center mr-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="text-left">Compete with fellow cricket enthusiasts</p>
            </div>
          </div>
          <p className="text-blue-200 mt-8">Already have an account?</p>
          <Link 
            to="/login" 
            className="mt-2 inline-block px-6 py-3 bg-white text-blue-700 rounded-lg font-medium hover:bg-blue-50 transition shadow-md"
          >
            Sign In
          </Link>
        </div>
      </div>
      
      {/* Right Side - Registration Form */}
      <div className="w-full md:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
            <div className="inline-block p-4 rounded-full bg-blue-100 dark:bg-blue-900 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-blue-600 dark:text-blue-300" viewBox="0 0 20 20" fill="currentColor">
                <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-2">Create Account</h2>
            <p className="text-gray-600 dark:text-gray-400">Join the GamePreBattle community</p>
          </div>
          
          {error && (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded">
              <div className="flex items-center">
                <svg className="h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <p>{error}</p>
              </div>
            </div>
          )}
          
          {referralMessage && (
            <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-6 rounded">
              <div className="flex items-center">
                <svg className="h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <p>{referralMessage}</p>
              </div>
            </div>
          )}
          
            <div className="flex flex-col space-y-4 mt-8">
              <p className="text-center text-gray-600 dark:text-gray-400 mb-4">Choose how you want to create your account:</p>
              
              {/* Google Sign-Up Button */}
              <button
                onClick={handleGoogleSignUp}
                disabled={loading}
                className="w-full flex items-center justify-center py-3 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <g transform="matrix(1, 0, 0, 1, 27.009001, -39.238998)">
                    <path fill="#4285F4" d="M -3.264 51.509 C -3.264 50.719 -3.334 49.969 -3.454 49.239 L -14.754 49.239 L -14.754 53.749 L -8.284 53.749 C -8.574 55.229 -9.424 56.479 -10.684 57.329 L -10.684 60.329 L -6.824 60.329 C -4.564 58.239 -3.264 55.159 -3.264 51.509 Z" />
                    <path fill="#34A853" d="M -14.754 63.239 C -11.514 63.239 -8.804 62.159 -6.824 60.329 L -10.684 57.329 C -11.764 58.049 -13.134 58.489 -14.754 58.489 C -17.884 58.489 -20.534 56.379 -21.484 53.529 L -25.464 53.529 L -25.464 56.619 C -23.494 60.539 -19.444 63.239 -14.754 63.239 Z" />
                    <path fill="#FBBC05" d="M -21.484 53.529 C -21.734 52.809 -21.864 52.039 -21.864 51.239 C -21.864 50.439 -21.724 49.669 -21.484 48.949 L -21.484 45.859 L -25.464 45.859 C -26.284 47.479 -26.754 49.299 -26.754 51.239 C -26.754 53.179 -26.284 54.999 -25.464 56.619 L -21.484 53.529 Z" />
                    <path fill="#EA4335" d="M -14.754 43.989 C -12.984 43.989 -11.404 44.599 -10.154 45.789 L -6.734 42.369 C -8.804 40.429 -11.514 39.239 -14.754 39.239 C -19.444 39.239 -23.494 41.939 -25.464 45.859 L -21.484 48.949 C -20.534 46.099 -17.884 43.989 -14.754 43.989 Z" />
                  </g>
                </svg>
                {loading ? 'Processing...' : 'Continue with Google'}
              </button>
              
              {/* Apple Sign-Up Button */}
              <button
                onClick={handleAppleSignUp}
                disabled={loading}
                className="w-full flex items-center justify-center py-3 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg className="h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701z" />
                </svg>
                {loading ? 'Processing...' : 'Continue with Apple'}
              </button>
            </div>
            
            <div className="mt-6 text-center">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                By registering, you agree to our 
                <Link to="/terms" className="text-blue-600 dark:text-blue-400 ml-1">Terms & Conditions</Link> 
                and <Link to="/privacy" className="text-blue-600 dark:text-blue-400">Privacy Policy</Link>.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Step 2: Enter Username
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 dark:bg-gray-900">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden p-8">
        <div className="text-center mb-10">
          <div className="inline-block p-4 rounded-full bg-blue-100 dark:bg-blue-900 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-blue-600 dark:text-blue-300" viewBox="0 0 20 20" fill="currentColor">
              <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-2">Complete Your Profile</h2>
          <p className="text-gray-600 dark:text-gray-400">Just one more step to join the community</p>
        </div>
        
        {error && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded">
            <div className="flex items-center">
              <svg className="h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <p>{error}</p>
            </div>
          </div>
        )}
        
        {referralMessage && (
          <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-6 rounded">
            <div className="flex items-center">
              <svg className="h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <p>{referralMessage}</p>
            </div>
          </div>
        )}
        
        <form onSubmit={handleSubmitUsername} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" htmlFor="username">
                Username
              </label>
              <input
                type="text"
                id="username"
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition"
                placeholder="Choose a username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Username must be at least 3 characters
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" htmlFor="referralCode">
                Referral Code <span className="text-gray-500 text-xs">(Optional)</span>
              </label>
              <input
                type="text"
                id="referralCode"
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition"
                placeholder="Enter referral code (if any)"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value)}
              />
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <label className="flex items-center space-x-3">
                <input 
                  type="checkbox" 
                  className="h-5 w-5 rounded text-blue-600 focus:ring-blue-500 border-gray-300" 
                  checked={isOver18}
                  onChange={(e) => setIsOver18(e.target.checked)}
                  required
                />
                <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                  I confirm that I am at least 18 years old
                </span>
              </label>
              <p className="mt-2 text-xs text-red-600 dark:text-red-400 font-medium pl-8">
                Important: This platform is restricted to users 18 years and older.
              </p>
            </div>
            
            <button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition shadow-md flex items-center justify-center"
              disabled={loading}
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                Processing...
                </>
            ) : 'Complete Registration'}
            </button>
          </form>
      </div>
    </div>
  );
};

export default Register; 